// House images for the pencil-in stock presets, generated once at pro quality
// and bundled; custom-named meals get the simmering-pot house image — contents
// a mystery, like the meal — rather than a stand-in pretending to be the dish.
// focusY is the object-position vertical %: each photo composes its subject at
// a different height, and wide card windows crop the 4:3 frame to a band, so a
// shared value slices either the plate (centered) or the top of the subject
// (bottom-biased). Values tuned per image against the live plan grid.
export interface PencilInImage {
  src: string;
  focusY: number;
}

const PRESET_IMAGES: Record<string, PencilInImage> = {
  'tacos': { src: '/images/pencil-in/tacos.jpg', focusY: 75 },
  'pizza night': { src: '/images/pencil-in/pizza-night.jpg', focusY: 60 },
  'takeout': { src: '/images/pencil-in/takeout.jpg', focusY: 60 },
  'eating out': { src: '/images/pencil-in/eating-out.jpg', focusY: 70 },
  'leftovers': { src: '/images/pencil-in/leftovers.jpg', focusY: 70 },
  'freezer meal': { src: '/images/pencil-in/freezer-meal.jpg', focusY: 70 },
  'breakfast for dinner': { src: '/images/pencil-in/breakfast-for-dinner.jpg', focusY: 75 },
  // Breakfast presets
  'cereal': { src: '/images/pencil-in/cereal.jpg', focusY: 60 },
  'muffins & pastries': { src: '/images/pencil-in/muffins-pastries.jpg', focusY: 45 },
  'eggs & toast': { src: '/images/pencil-in/eggs-toast.jpg', focusY: 60 },
  'pancakes': { src: '/images/pencil-in/pancakes.jpg', focusY: 28 },
  'smoothies': { src: '/images/pencil-in/smoothies.jpg', focusY: 60 },
  'bagels': { src: '/images/pencil-in/bagels.jpg', focusY: 60 },
  'yogurt & fruit': { src: '/images/pencil-in/yogurt-fruit.jpg', focusY: 60 },
  // Lunch presets (leftovers/takeout/eating out reuse the dinner images)
  'sandwiches': { src: '/images/pencil-in/sandwiches.jpg', focusY: 30 },
  'salad': { src: '/images/pencil-in/salad.jpg', focusY: 60 },
  'soup': { src: '/images/pencil-in/soup.jpg', focusY: 60 },
};

// Slot-specific overrides for preset names whose dinner image reads wrong in
// another slot: takeout boxes and a dinner-table spread are unmistakably
// dinner, so lunch gets its own takes. Names without an override fall through
// to the shared map.
const SLOT_PRESET_IMAGES: Partial<
  Record<'breakfast' | 'lunch' | 'dinner', Record<string, PencilInImage>>
> = {
  breakfast: {
    'freezer meal': { src: '/images/pencil-in/freezer-meal-breakfast.jpg', focusY: 60 },
  },
  lunch: {
    'takeout': { src: '/images/pencil-in/takeout-lunch.jpg', focusY: 60 },
    'eating out': { src: '/images/pencil-in/eating-out-lunch.jpg', focusY: 60 },
    'freezer meal': { src: '/images/pencil-in/freezer-meal-lunch.jpg', focusY: 60 },
  },
};

// Custom-named meals get a per-slot house image that says the meal without
// claiming a dish (the dinner one is the simmering pot; breakfast is coffee
// by an empty plate, lunch a closed paper lunch bag)
const FALLBACK_IMAGES: Record<'breakfast' | 'lunch' | 'dinner', PencilInImage> = {
  breakfast: { src: '/images/pencil-in/penciled-breakfast.jpg', focusY: 0 },
  lunch: { src: '/images/pencil-in/penciled-lunch.jpg', focusY: 30 },
  dinner: { src: '/images/pencil-in/penciled.jpg', focusY: 60 },
};

export const pencilInImageFor = (
  name: string,
  mealType: 'breakfast' | 'lunch' | 'dinner' = 'dinner'
): PencilInImage => {
  const key = name.trim().toLowerCase();
  return (
    SLOT_PRESET_IMAGES[mealType]?.[key] ??
    PRESET_IMAGES[key] ??
    FALLBACK_IMAGES[mealType]
  );
};
